import {useCallback} from 'react';

import {GetConsultProtocolsModel} from 'domain/usecases';
import {HomeEnum} from 'presentation/constants';
import {stringfyDate} from 'presentation/utils';
import {useLocation, useNavigate} from 'react-router-dom';
import {useLazyScroll} from 'presentation/hooks/useLazyScroll';
import {useQueryParams} from '../../hooks';
import {SIZE_LIMIT} from '../constants';

import {FilteredItemsListContents} from '../types';
import {useFilterByDatePicker} from './useFilterByDatePicker';
import {useFilters} from './useFilters';
import {useListProtocolsQuery} from '../queries/useListProtocolsQuery';

export function useConsultProtocols() {
	const location = useLocation();
	const navigate = useNavigate();

	const {SEARCH_PARAMS} = useQueryParams();
	const {orderBy, protocolType, status, initialDate, finalDate} = SEARCH_PARAMS;

	const {getThreeMonthsAgo} = useFilterByDatePicker();
	const {isFilterChanged} = useFilters();

	const handleBack = useCallback(() => {
		if (location.state?.pathname && location.state?.search) {
			navigate(`${location.state.pathname}${location.state.search}`);
			return;
		}
		if (location.state?.includes('/servicos')) {
			navigate(location.state);
			return;
		}
		navigate(HomeEnum.HOME_PAGE);
	}, [location]);

	const {
		data: protocolsList,
		isFetching,
		error: isProtocolsHasError,
		refetch,
	} = useListProtocolsQuery({
		initialDate: initialDate || stringfyDate(getThreeMonthsAgo()),
		finalDate: finalDate || stringfyDate(new Date()),
	});

	const isProtocolsListLoading = isFetching || isFilterChanged;

	const consultProtocolsResponses = protocolsList ? protocolsList.items : [];

	const consultProtocolsFilters: FilteredItemsListContents['filters'] = protocolsList
		? protocolsList.filters
		: {protocolsTypes: [], statusTypes: []};

	const isProtocolsListEmpty = protocolsList && !consultProtocolsResponses.length;

	const parseDate = (dateString: string): Date => {
		const [datePart, timePart] = dateString.split(' ');
		const [day, month, year] = datePart.split('/');
		const time = timePart || '00:00';
		const isoDate = `${year}-${month}-${day}T${time}`;

		return new Date(isoDate);
	};

	const sortProtocols = (
		protocols: GetConsultProtocolsModel['protocolos'],
		orderByParam: string,
	): GetConsultProtocolsModel['protocolos'] =>
		protocols.toSorted((a, b) => {
			const dateA = parseDate(a.dtAtendimento);
			const dateB = parseDate(b.dtAtendimento);
			const comparison = dateA.getTime() - dateB.getTime();

			return orderByParam === 'ASC' ? comparison : -comparison;
		});

	const filterConsultProtocols = (protocols: GetConsultProtocolsModel['protocolos']) => {
		const filteredProtocols = protocols.filter(
			protocol =>
				(!protocolType || protocol.tipoProtocolo === protocolType) &&
				(!status || protocol.status === Number(status)),
		);

		return sortProtocols(filteredProtocols, orderBy);
	};

	const {items, scrollableContentRef, loadingNewPageState, handleVisible} = useLazyScroll<
		GetConsultProtocolsModel['protocolos'][0]
	>({
		itemsList: filterConsultProtocols(consultProtocolsResponses),
		itemsPerPage: SIZE_LIMIT,
		loadingTime: consultProtocolsResponses && consultProtocolsResponses.length > 0 ? 600 : 0,
	});

	return {
		handleBack,
		protocolsList,
		isProtocolsListLoading,
		isProtocolsListEmpty,
		isProtocolsHasError,
		loadingNewPageState,
		refetch,
		consultProtocolsResponses,
		consultProtocolsFilters,
		items,
		scrollableContentRef,
		filterConsultProtocols,
		handleVisible,
	};
}
